import type { PoseFrame } from '@tbc/core';
import { PoseRunner } from '@tbc/models';

/**
 * Same Promise-based API as the original worker proxy, but inference runs on
 * the main thread. We tried a module-worker variant first and hit a MediaPipe
 * loader bug on Chrome Android prod builds (`self.import is not a function`).
 * Re-introduce a worker once the root cause is isolated (see ADR 0005).
 */
export class PoseRunnerProxy {
  private runner: PoseRunner | null = null;
  private ready: Promise<void>;

  constructor(private readonly numPoses: 1 | 2 = 2) {
    this.ready = this.init();
  }

  private async init(): Promise<void> {
    this.runner = await PoseRunner.create({
      runningMode: 'VIDEO',
      delegate: 'GPU',
      numPoses: this.numPoses,
    });
  }

  whenReady(): Promise<void> {
    return this.ready;
  }

  /** Convenience: returns the first detected pose only. */
  async detect(bitmap: ImageBitmap, timestampMs: number): Promise<PoseFrame | null> {
    const all = await this.detectMulti(bitmap, timestampMs);
    return all[0] ?? null;
  }

  /** Returns up to `numPoses` detected poses. Caller must not close the bitmap. */
  async detectMulti(bitmap: ImageBitmap, timestampMs: number): Promise<PoseFrame[]> {
    try {
      if (!this.runner) return [];
      return this.runner.detectFrameMulti(bitmap, timestampMs);
    } finally {
      bitmap.close();
    }
  }

  dispose(): void {
    this.runner?.close();
    this.runner = null;
  }
}
